import React, { useState, useEffect } from "react";
import { apiGet } from "../../services/api";
import "./Pharmacy.css";

interface DrugItem {
  id: number | string;
  name: string;
  stock_qty?: number;
  strength?: string;
}

export default function DrugListPanel() {
  const [drugs, setDrugs] = useState<DrugItem[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [drugData, groupData] = await Promise.all([
          apiGet("/api/pharmacy/drugs/all"),
          apiGet("/api/pharmacy/drug-groups")
        ]);
        setDrugs(Array.isArray(drugData) ? (drugData as DrugItem[]) : []);
        setGroups(Array.isArray(groupData) ? groupData : []);
      } catch (e) {
        console.error("Failed to load inventory data", e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredDrugs = drugs.filter((drug) => {
    const matchesSearch = drug.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!selectedGroupId) return matchesSearch;

    // Filter by group: find if drug is in the selected group
    const group = groups.find(g => String(g.id) === selectedGroupId);
    const isInGroup = group?.drugs?.some((gd: any) => String(gd.id) === String(drug.id));
    return matchesSearch && isInGroup;
  });

  return (
    <div className="drug-list-panel">
      <div className="drug-list-header">
        <h4>📋 Drug Inventory</h4>
        <div className="drug-list-filters">
          <input
            type="text"
            placeholder="🔍 Search drugs..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="drug-search-input"
          />
          <select
            className="group-filter-select"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
          >
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.group_name || g.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="drug-list-content">
        {loading ? (
          <div className="drug-list-loading">Loading...</div>
        ) : filteredDrugs.length === 0 ? (
          <div className="drug-list-empty">
            {searchQuery ? "No drugs found" : "No drugs available"}
          </div>
        ) : (
          <div className="drug-list-items">
            {filteredDrugs.map((drug) => (
              <div key={drug.id} className="drug-list-item">
                <div className="drug-item-name">{drug.name}</div>
                <div className="drug-item-meta">
                  <span className="drug-stock">
                    Stock: <strong>{drug.stock_qty ?? 0}</strong> units
                  </span>
                  {drug.strength && <span className="drug-strength">{drug.strength}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
